import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, AlertTriangle, Clock, FileText, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

function pct1(v) {
  return v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`;
}

export default function Project360() {
  const { projectId } = useProjectCtx();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/360`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <p className="text-sm text-muted-foreground" data-testid="project-360-loading">Loading Project 360…</p>;

  const { header, tiles, progress, budget_vs_actual, recent_cost_entries, latest_daily_reports, open_issues, open_delays, open_alerts, procurement_overdue, upcoming_milestones } = data;

  const progressRows = [
    { label: "Engineering", planned: progress.engineering_planned_pct, actual: progress.engineering_actual_pct },
    { label: "Procurement", planned: progress.procurement_planned_pct, actual: progress.procurement_actual_pct },
    { label: "Construction", planned: progress.construction_planned_pct, actual: progress.construction_actual_pct },
    { label: "Overall", planned: progress.overall_planned_pct, actual: progress.overall_actual_pct },
  ];

  return (
    <div className="space-y-6" data-testid="project-360-page">
      <PageHeader
        title="Project 360"
        description={`${header.name} · ${header.code}${header.company_name ? ` · ${header.company_name}` : ""}${header.location ? ` · ${header.location}` : ""}`}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="project-360-tiles">
        <Tile label="Total budget" value={fmtNum(tiles.total_budget, 2)} />
        <Tile label="Actual cost" value={fmtNum(tiles.actual_cost, 2)} />
        <Tile label="Committed" value={fmtNum(tiles.committed, 2)} />
        <Tile label="Variance" value={fmtNum(tiles.variance, 2)} tone={Number(tiles.variance) < 0 ? "rose" : "emerald"} />
        <Tile label="Estimate to complete" value={fmtNum(tiles.estimate_to_complete, 2)} />
        <Tile label="Forecast at completion" value={fmtNum(tiles.forecast_at_completion, 2)} />
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="project-360-progress">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Progress — Planned vs Actual</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {progressRows.map((r) => (
            <div key={r.label} className="rounded-lg border border-border p-3" data-testid={`progress-row-${r.label.toLowerCase()}`}>
              <p className="text-xs font-semibold text-foreground">{r.label}</p>
              <p className="mt-1 font-mono text-xl font-bold text-foreground">{pct1(r.actual)}</p>
              <p className="text-[11px] text-muted-foreground">Planned {pct1(r.planned)}</p>
            </div>
          ))}
        </div>
        {progress.certified_pct !== null && (
          <div className="mt-3 flex flex-wrap items-center gap-4 rounded-lg border border-border p-3" data-testid="progress-certified-gap">
            <div><p className="text-[11px] text-muted-foreground">Certified (RA billed)</p><p className="font-mono text-lg font-bold text-foreground">{pct1(progress.certified_pct)}</p></div>
            <div>
              <p className="text-[11px] text-muted-foreground">Certified vs construction gap</p>
              <p className={`font-mono text-lg font-bold ${Number(progress.certified_vs_construction_gap) < 0 ? "text-rose-400" : "text-emerald-400"}`}>{pct1(progress.certified_vs_construction_gap)}</p>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border p-4"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Budget vs Actual by WBS</h2></div>
        {budget_vs_actual.length === 0 ? (
          <EmptyState title="No budget rows yet" testId="project-360-bva-empty" />
        ) : (
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card"><tr className="border-b border-border text-left text-xs text-muted-foreground"><th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3 text-right">Budget</th><th className="p-3 text-right">Actual</th><th className="p-3 text-right">Committed</th><th className="p-3 text-right">Variance</th></tr></thead>
              <tbody>
                {budget_vs_actual.map((row, i) => (
                  <tr key={row.wbs_item_id || i} className="border-b border-border/50" data-testid={`project-360-bva-row-${i}`}>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{row.code || "—"}</td>
                    <td className="p-3 text-foreground">{row.name || "—"}</td>
                    <td className="p-3 text-right font-mono text-xs">{fmtNum(row.budget, 2)}</td>
                    <td className="p-3 text-right font-mono text-xs">{fmtNum(row.actual, 2)}</td>
                    <td className="p-3 text-right font-mono text-xs text-muted-foreground">{fmtNum(row.committed, 2)}</td>
                    <td className={`p-3 text-right font-mono text-xs font-semibold ${Number(row.variance) < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtNum(row.variance, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ListCard title="Open Issues" icon={AlertTriangle} testId="project-360-open-issues" items={open_issues} empty="No open issues" onClick={() => navigate(`/projects/${projectId}/issues`)}
          render={(i) => <>{i.description} <span className="text-muted-foreground">({i.priority})</span></>} />
        <ListCard title="Open Delays" icon={Clock} testId="project-360-open-delays" items={open_delays} empty="No open delays" onClick={() => navigate(`/projects/${projectId}/delays`)}
          render={(d) => <>{d.category} — {d.reason} <span className="text-muted-foreground">({d.impact_days}d)</span></>} />
        <ListCard title="Open Alerts" icon={ShieldAlert} testId="project-360-open-alerts" items={open_alerts} empty="No open alerts" onClick={() => navigate(`/projects/${projectId}/alerts`)}
          render={(a) => <>{a.title} <span className="text-muted-foreground">({a.severity})</span></>} />
        <ListCard title="Procurement Overdue" icon={ShieldAlert} testId="project-360-procurement-overdue" items={procurement_overdue} empty="Nothing overdue" onClick={() => navigate(`/projects/${projectId}/alerts`)}
          render={(a) => <>{a.title}</>} />
        <ListCard title="Latest Daily Reports" icon={FileText} testId="project-360-daily-reports" items={latest_daily_reports} empty="No reports yet" onClick={() => navigate(`/projects/${projectId}/daily-reports`)}
          render={(r) => <>{r.number} · {fmtDate(r.report_date)} <span className="text-muted-foreground">({r.status})</span></>} />
        <ListCard title="Upcoming Milestones" icon={CalendarDays} testId="project-360-milestones" items={upcoming_milestones} empty="No upcoming milestones" onClick={() => navigate(`/projects/${projectId}/schedule`)}
          render={(m) => <>{m.name} <span className="text-muted-foreground">— due {fmtDate(m.end_date)}</span></>} />
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border p-4"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Recent Cost Entries</h2></div>
        {recent_cost_entries.length === 0 ? (
          <EmptyState title="No cost entries yet" testId="project-360-cost-entries-empty" />
        ) : (
          <ul className="divide-y divide-border" data-testid="project-360-recent-cost-entries">
            {recent_cost_entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between p-3 text-sm">
                <span className="text-muted-foreground">{fmtDate(e.entry_date)} · {e.category} · {e.description || "—"}</span>
                <span className="font-mono text-xs font-semibold text-foreground">{fmtNum(e.amount, 2)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const toneClass = tone === "rose" ? "text-rose-400" : tone === "emerald" ? "text-emerald-400" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`project-360-tile-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 font-mono text-lg font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function ListCard({ title, icon: Icon, testId, items, empty, render, onClick }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={testId}>
      <button onClick={onClick} className="flex w-full items-center justify-between border-b border-border p-4 text-left hover:bg-accent/30" data-testid={`${testId}-header`}>
        <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</span>
      </button>
      {items.length === 0 ? (
        <p className="p-4 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.slice(0, 6).map((item, i) => (
            <li key={item.id || i} className="p-3 text-xs text-foreground">{render(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
